import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { formatCompact, safePercent } from "../../utils/format-number";
import StatNumber from "./stat-number";
import { ChartEmpty, ChartTooltip, SEQUENTIAL } from "./chart-bits";

type Props = { data: any[] | null };

/** Buckets in ascending order — the ramp only means anything if the order is fixed. */
const BUCKETS = [
  "£0-£100",
  "£100-£200",
  "£200-£300",
  "£300-£400",
  "£400-£500",
  "Over £500",
];

/**
 * Chases by outstanding balance — donut plus a ranked breakdown.
 *
 * These buckets are an ordered scale, not distinct categories, so the colour job is
 * **sequential**: one hue, light→dark with magnitude. A categorical palette would
 * imply the bands are unrelated and throw away the ordering.
 *
 * The donut carries the shape of the split at a glance; the bars underneath do the
 * comparing, since judging six wedge angles against each other is far harder than
 * reading six bars off a shared baseline. Each bucket keeps the same ramp step in
 * both, so the two halves read as one figure.
 */
export default function BalanceBuckets({ data }: Props) {
  const rows = data ?? [];

  const values = BUCKETS.map((label, i) => ({
    label,
    color: SEQUENTIAL[i],
    count: Number(rows.find((r) => r?.amountRange === label)?.totalChases ?? 0),
  }));

  const total = values.reduce((sum, v) => sum + v.count, 0);
  if (!total) return <ChartEmpty />;

  const max = Math.max(...values.map((v) => v.count));
  const slices = values.filter((v) => v.count > 0);

  return (
    <div className="flex flex-col gap-4">
      {/* ---------- Donut ---------- */}
      <div className="relative h-[150px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <ChartTooltip
                    rows={[
                      {
                        label: payload[0]?.payload?.label,
                        color: payload[0]?.payload?.color,
                        value: payload[0]?.value,
                      },
                    ]}
                  />
                ) : null
              }
            />
            <Pie
              data={slices}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={47}
              outerRadius={72}
              // 2px of surface between wedges — the gap separates, not an outline.
              stroke="var(--color-surface)"
              strokeWidth={2}
              paddingAngle={1}
              startAngle={90}
              endAngle={-270}
            >
              {slices.map((s) => (
                <Cell key={s.label} fill={s.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Total in the hole — the one figure the donut can't encode. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[22px] font-bold text-text-primary leading-none">
            {formatCompact(total)}
          </span>
          <span className="mt-1 text-[11px] font-medium text-light-gray">
            chases
          </span>
        </div>
      </div>

      {/* ---------- Ranked breakdown ---------- */}
      <div className="flex flex-col gap-2.5">
        {values.map((v) => (
          <div key={v.label} className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 w-[86px] shrink-0">
              <span
                aria-hidden
                className="w-2 h-2 rounded-[3px] shrink-0"
                style={{ background: v.color }}
              />
              <span className="text-[11px] font-medium text-light-gray truncate">
                {v.label}
              </span>
            </span>

            <div className="flex-1 h-1.5 rounded-full bg-overlay-mid overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${safePercent(v.count, max)}%`,
                  background: v.color,
                }}
              />
            </div>

            <span className="w-[64px] shrink-0 text-[12px] font-semibold text-text-primary tabular-nums text-right">
              <StatNumber value={v.count} unit="chases" />
              <span className="ml-1 text-light-gray font-medium">
                {safePercent(v.count, total).toFixed(0)}%
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
